import { Lock } from 'lucide-react';
import type { AcessoDef, AcessoLinha, OpcoesConcessao, Papel } from '@triade/contracts';
import { DESCRICAO_PAPEL, rotuloPapel } from '@/components/app-shell/papel';
import { chaveAcesso, ordenarAcessos, rotuloAcesso } from './tipos';

export interface EditorAcessosProps {
  /** O que quem edita pode conceder, por onde (vem de GET /usuarios; o teto e conferido pela API). */
  opcoes: OpcoesConcessao;
  /** Acessos escolhidos (so os que quem edita pode mexer). */
  valor: readonly AcessoDef[];
  onChange: (proximo: AcessoDef[]) => void;
  /** Acessos que o usuario ja tem e quem edita NAO pode mexer (acima do teto): aparecem travados. */
  travados?: readonly AcessoLinha[];
  desabilitado?: boolean;
  /** Prefixo dos ids (varios editores na mesma pagina). */
  idPrefixo: string;
}

/** Descricao dos papeis oferecidos que tem uma (ex.: Representante externo), abaixo da grade. */
function DescricoesPapeis({ papeis, idPrefixo }: { papeis: readonly Papel[]; idPrefixo: string }) {
  const comDescricao = papeis.filter((p) => DESCRICAO_PAPEL[p]);
  if (comDescricao.length === 0) return null;
  return (
    <ul className="mt-2 flex flex-col gap-0.5" data-testid="descricao-papeis">
      {comDescricao.map((p) => (
        <li key={p} id={`${idPrefixo}-desc-${p}`} className="text-caption text-ink-muted">
          <span className="font-medium text-ink-secondary">{rotuloPapel(p)}:</span> {DESCRICAO_PAPEL[p]}
        </li>
      ))}
    </ul>
  );
}

function Caixa({
  id,
  rotulo,
  marcado,
  desabilitado,
  onAlternar,
}: {
  id: string;
  rotulo: string;
  marcado: boolean;
  desabilitado?: boolean;
  onAlternar: () => void;
}) {
  return (
    <label htmlFor={id} className="inline-flex cursor-pointer items-center gap-2 text-small text-ink-body has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-60">
      <input id={id} type="checkbox" checked={marcado} disabled={desabilitado} onChange={onAlternar} className="size-4 accent-primary" />
      {rotulo}
    </label>
  );
}

/**
 * Grade de acessos: tenant inteiro, consultor por grupo e, por empresa, os papeis que quem edita pode conceder (varios por empresa).
 * So esconde opcao: quem nega e a API (403 TETO_DE_PAPEL).
 */
export function EditorAcessos({ opcoes, valor, onChange, travados = [], desabilitado = false, idPrefixo }: EditorAcessosProps) {
  const marcados = new Set(valor.map(chaveAcesso));
  const alternar = (a: AcessoDef) => {
    const k = chaveAcesso(a);
    onChange(ordenarAcessos(marcados.has(k) ? valor.filter((v) => chaveAcesso(v) !== k) : [...valor, a]));
  };
  const semOpcao = opcoes.tenant.length === 0 && opcoes.grupos.length === 0 && opcoes.empresas.length === 0;

  return (
    <div className="flex flex-col gap-4" data-testid="editor-acessos">
      {semOpcao && travados.length === 0 && <p className="text-small text-ink-muted">Seu perfil não permite conceder acessos.</p>}

      {opcoes.tenant.length > 0 && (
        <fieldset className="rounded-xl border border-border p-3">
          <legend className="px-1 text-caption font-medium text-ink-secondary">Organização inteira</legend>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {opcoes.tenant.map((papel) => (
              <Caixa
                key={papel}
                id={`${idPrefixo}-tenant-${papel}`}
                rotulo={`${rotuloPapel(papel)} (todas as empresas)`}
                marcado={marcados.has(chaveAcesso({ papel }))}
                desabilitado={desabilitado}
                onAlternar={() => alternar({ papel, empresaId: null, grupoId: null })}
              />
            ))}
          </div>
        </fieldset>
      )}

      {opcoes.grupos.length > 0 && (
        <fieldset className="rounded-xl border border-border p-3">
          <legend className="px-1 text-caption font-medium text-ink-secondary">Consultor por grupo de empresas</legend>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {opcoes.grupos.flatMap((g) =>
              g.papeis.map((papel: Papel) => (
                <Caixa
                  key={`${g.id}-${papel}`}
                  id={`${idPrefixo}-grupo-${g.id}-${papel}`}
                  rotulo={`${rotuloPapel(papel)} · grupo ${g.nome}`}
                  marcado={marcados.has(chaveAcesso({ papel, grupoId: g.id }))}
                  desabilitado={desabilitado}
                  onAlternar={() => alternar({ papel, empresaId: null, grupoId: g.id })}
                />
              )),
            )}
          </div>
        </fieldset>
      )}

      {opcoes.empresas.map((e) => (
        <fieldset key={e.id} className="rounded-xl border border-border p-3" data-testid={`acessos-empresa-${e.id}`}>
          <legend className="px-1 text-caption font-medium text-ink-secondary">{e.nome}</legend>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {e.papeis.map((papel) => (
              <Caixa
                key={papel}
                id={`${idPrefixo}-emp-${e.id}-${papel}`}
                rotulo={rotuloPapel(papel)}
                marcado={marcados.has(chaveAcesso({ papel, empresaId: e.id }))}
                desabilitado={desabilitado}
                onAlternar={() => alternar({ papel, empresaId: e.id, grupoId: null })}
              />
            ))}
          </div>
          <DescricoesPapeis papeis={e.papeis} idPrefixo={`${idPrefixo}-emp-${e.id}`} />
        </fieldset>
      ))}

      {travados.length > 0 && (
        <div data-testid="acessos-travados">
          <p className="mb-1.5 text-caption font-medium text-ink-secondary">Acessos acima do seu perfil (só leitura)</p>
          <ul className="flex flex-wrap gap-2">
            {travados.map((a) => (
              <li key={a.id} className="inline-flex items-center gap-1.5 rounded-full bg-line-soft px-2.5 py-1 text-caption text-ink-body">
                <Lock aria-hidden="true" className="size-3" />
                {rotuloAcesso(a)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
